'use client';

import React from 'react';
import { Typography } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

export interface CodeSetSelectorSectionProps {
  /** Section label, e.g. "Company" / "Personal". */
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** Overrides for the scrollable content container. */
  contentClassName?: string;
  'data-testid'?: string;
}

/**
 * A collapsible Personal/Company section used by the code-set selector dropdowns.
 * Keeps the accordion header (chevron + label) consistent across selectors.
 */
const CodeSetSelectorSection: React.FC<CodeSetSelectorSectionProps> = ({
  label,
  isOpen,
  onToggle,
  children,
  contentClassName = 'max-h-56 overflow-y-auto',
  'data-testid': testId,
}) => (
  <div className="w-full border-t border-neutral-200">
    <button
      type="button"
      className="flex items-center w-full px-4 py-2 hover:bg-neutral-100 cursor-pointer"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={`Toggle ${label.toLowerCase()} section`}
      data-testid={testId}
    >
      <div className="w-3 h-3 flex items-center justify-center mr-2">
        <Icon
          icon={isOpen ? faChevronDown : faChevronRight}
          className="text-neutral-600 text-xs"
          fixedWidth
          aria-hidden="true"
        />
      </div>
      <Typography className="text-sm font-semibold text-neutral-600">
        {label}
      </Typography>
    </button>

    {isOpen && (
      <div className={contentClassName} role="group" aria-label={label}>
        {children}
      </div>
    )}
  </div>
);

export default CodeSetSelectorSection;
